import client from '../../../../axios-client';
import { dateFormatter } from '../../../../translations';
import { MODE_FROM_NAME, MODE_HAND_DRAWN } from '../../../../signature-utils';

export default {
  updateActiveCancellation: (context, payload) => {
    context.commit('clearPaymentMethods');
    context.commit('clearFlashes');
    context.commit('clearSignature');
    const activeCancellation = context.getters.getSubCancellation(payload.id);
    context.commit('setActiveCancellation', activeCancellation);
    context.commit('setPaymentMethods', activeCancellation.paymentMethods);
    context.commit(
      'signatureMode',
      true === activeCancellation.drawnSignatureRequired ? MODE_HAND_DRAWN : MODE_FROM_NAME,
    );
    context.commit('requireDrawnSignature', activeCancellation.drawnSignatureRequired);
    context.dispatch('updateSignature');
    context.dispatch('updateAdditionalFormFields', activeCancellation.additionalFields);
    context.dispatch('updateTotalPrice');
    context.dispatch('updateReasonToCancel', activeCancellation.reasonToCancelRequired);
    context.commit('setupComplete', true);
  },
  setupGeneric: async (context, cancellation) => {
    context.commit('generic', true);
    context.commit('setActiveCancellation', cancellation);
    context.commit('signatureMode', true === cancellation.drawnSignatureRequired ? MODE_HAND_DRAWN : MODE_FROM_NAME);
    context.commit('requireDrawnSignature', cancellation.drawnSignatureRequired);
    await context.dispatch('setup', cancellation);
    context.commit('setupComplete', true);
  },
  setupSpecific: async (context, cancellation) => {
    context.commit('generic', false);
    if ('subCancellations' in cancellation && 0 < cancellation.subCancellations.length) {
      cancellation.subCancellations.forEach(sub => context.dispatch('addCancellation', sub));
      await context.dispatch('setup', cancellation);
    } else {
      context.commit('setActiveCancellation', cancellation);
      context.commit('signatureMode', true === cancellation.drawnSignatureRequired ? MODE_HAND_DRAWN : MODE_FROM_NAME);
      context.commit('requireDrawnSignature', cancellation.drawnSignatureRequired);
      await context.dispatch('updateReasonToCancel', cancellation.reasonToCancelRequired);
      await context.dispatch('updateAdditionalFormFields', cancellation.additionalFields);
      await context.dispatch('setup', cancellation);
      context.commit('setupComplete', true);
    }
  },
  setup: async (context, cancellation) => {
    context.commit('setCancellation', cancellation);
    context.commit('setPaymentMethods', cancellation.paymentMethods);
    context.commit('setOptionalProduct', cancellation.optionalProduct);
  },
  getCancellationData: (context, payload) => {
    client
      .getSubCancellations(payload.id)
      .then((response) => {
        const data = response.data.data['0'];
        context.dispatch('addCancellation', data);
      });
  },
  addCancellation: (context, payload) => {
    context.commit('addSubCancellation', { id: payload.id, subCancellation: payload });
    context.commit('addCancellationOption', { id: payload.id, text: payload.cancellationText });
  },
  submit: (context) => {
    context.commit('showErrors', true);
    context.commit('showPaymentTypeErrors', true);
    if (0 === context.getters.getErrorCount + context.getters.getPaymentTypeErrorCount) {
      context.commit('clearFlashes');
      context.commit('isLoading', true);

      return true;
    }
    const tryToScroll = entries => {
      entries.forEach(entry => {
        if (true === entry.validity) return true;

        try {
          context.getters.getInputRef(entry.state).scrollIntoView();
          // eslint-disable-next-line no-restricted-globals
          scrollBy(0, -100);
        } catch (e) {
          if (true === context.getters.hasInputRef('customerFirstName')) {
            context.getters.getInputRef('customerFirstName').scrollIntoView();
            // eslint-disable-next-line no-restricted-globals
            scrollBy(0, -100);
          }
        }

        return false;
      });
    };
    return tryToScroll(Object.entries(context.getters.getFormFieldValidityStates))
      && tryToScroll(Object.entries(context.getters.getPaymentTypeValidity));
  },
  focusInputRef: (context, name) => {
    try {
      context.getters.getInputRef(name).focus();
    } catch (TypeError) {
      // there is no input ref for this name, valid outcome
    }
  },
  /**
   * Checks whether signature can be updated from name, and then dispatches the update.
   * @param context
   */
  updateSignature: (context) => {
    if (context.getters.isSignaturePadDefined && MODE_FROM_NAME === context.getters.signatureMode) {
      context.dispatch('updateSignatureFromName');
    }
  },
  addFormField: (context, payload) => {
    if (Object.keys(context.getters.getPaymentType).includes(payload.name)) {
      context.commit('addPaymentType', payload);
    } else {
      context.commit('addFormField', payload);
    }
  },
  updateFormField: (context, payload) => {
    if (payload.name === context.getters.getIbanParameters.propertyName) {
      context.commit('updateIbanFormField', payload);
      if (Object.keys(context.getters.getPaymentType).includes(payload.name)) {
        context.commit('updatePaymentTypeField', payload);
      }
    } else if (payload.name === context.getters.getReasonToCancelParameters.propertyName) {
      context.commit('updateReasonToCancelFormField', payload);
    } else if (Object.keys(context.getters.getFormFields).includes(payload.name)) {
      context.commit('updateFormField', payload);
      if (
        context.getters.isSignaturePadDefined
        && MODE_FROM_NAME === context.getters.signatureMode
        && ('customerFirstName' === payload.name || 'customerLastName' === payload.name)
      ) {
        context.dispatch('updateSignatureFromName');
      }
    } else {
      context.commit('updateAdditionalFormField', payload);
    }
  },
  updateAdditionalFormFields: (context, payload) => {
    context.commit('clearAdditionalFormFieldValues');
    context.commit('clearAdditionalFormFields');
    context.commit('addAdditionalFormFields', payload);
    context.commit('addAdditionalFormFieldValues', payload);
  },
  validateAddress: (context) => {
    const postcode = context.getters.getFormField('customerPostcode');
    const country = {
      netherlands: context.getters.isDutchPostcode,
      belgium: context.getters.isBelgianPostcode,
    };
    context.commit('updatePostcodeCountry', country);
    const houseNumber = context.getters.getFormField('customerHouseNumber');
    let houseNumberExtension = context.getters.getFormField('customerHouseNumberExtension');
    if (!houseNumberExtension) {
      houseNumberExtension = '';
    }
    if (!(postcode && houseNumber)) {
      return context.dispatch('setAndValidateAddress', false);
    }
    if (country.netherlands) {
      context.commit('updateFormFieldValidity', {
        name: 'customerStreet',
        valid: true,
      });
      return context.dispatch('getAddress', {
        nl_sixpp: postcode,
        postcode,
        streetnumber: houseNumber,
        houseNumber,
        houseNumberExtension,
        country: 'NL',
      });
    }
    if (country.belgium) {
      context.commit('updateFormFieldValidity', {
        name: 'customerStreet',
        valid: false,
      });
      return context.dispatch('getAddress', {
        be_fourpp: postcode,
        houseNumber,
        postcode,
        country: 'BE',
      });
    }
    return context.dispatch('setAndValidateAddress', {
      country: null,
      valid: false,
    });
  },
  getAddress: (context, payload) => {
    const address = context.getters.cacheFetch(payload);
    if (undefined === address) {
      return context.dispatch('makeAddressApiCall', payload);
    }
    return context.dispatch('setAndValidateAddress', address);
  },
  makeAddressApiCall: (context, payload) => {
    context.getters.getClient.pro6ppApiCall(payload)
      .then((data) => {
        if (!('results' in data) || !('status' in data)) {
          throw new Error(`Invalid response format: ${JSON.stringify(data)}`);
        }
        if ('error' === data.status) throw new Error(data.error.message);
        // eslint-disable-next-line prefer-destructuring
        const address = data.results[0];
        context.commit('cacheSave', {
          postcode: payload.postcode,
          street: payload.street,
          houseNumber: payload.houseNumber,
          houseNumberExtension: payload.houseNumberExtension,
          value: address,
        });
        address.valid = true;
        address.country = payload.country;
        return context.dispatch('setAndValidateAddress', address);
      })
      .catch(() => {
        context.commit('cacheSave', {
          postcode: payload.postcode,
          houseNumber: payload.houseNumber,
          houseNumberExtension: payload.houseNumberExtension,
          value: false,
        });
        return context.dispatch('setAndValidateAddress', {
          country: payload.country,
          valid: false,
        });
      });
  },
  setAndValidateAddress: (context, payload) => {
    context.commit('setAddress', payload);
    const addressIsValid = payload.valid;
    context.commit('setAddressValidity', Boolean(addressIsValid));
    if (false === addressIsValid) {
      if ('BE' !== payload.country) {
        context.commit('updateFormField', {
          name: 'customerStreet',
          value: null,
        });
        context.commit('updateFormFieldValidity', {
          name: 'customerStreet',
          valid: false,
        });
      }
      context.commit('updateFormField', {
        name: 'customerCity',
        value: null,
      });
      context.commit('updateFormFieldValidity', {
        name: 'customerCity',
        valid: false,
      });
      return;
    }
    if ('BE' !== payload.country) {
      context.commit('updateFormField', {
        name: 'customerStreet',
        value: payload.street,
      });
      context.commit('updateFormFieldValidity', {
        name: 'customerStreet',
        valid: true,
      });
    }
    context.commit('updateFormField', {
      name: 'customerCity',
      value: payload.city,
    });
    context.commit('updateFormFieldValidity', {
      name: 'customerCity',
      valid: true,
    });
  },
  updateSignatureFromName: (context) => {
    const firstName = context.getters.getFormField('customerFirstName');
    const lastName = context.getters.getFormField('customerLastName');
    context.state.adivareSignaturePad.nameToSignature(firstName, lastName).then(data => {
      context.state.formFields.customerSignature = data;
      context.state.formFieldValidityStates.customerSignature = true;
    });
    context.commit('updateFormFieldValidity', { name: 'customerSignature', valid: true });
  },
  updateCancellationDate: (context, value) => {
    if (!(value instanceof Date)) {
      throw new Error('Expected value to be instance of Date, unable to process');
    }
    const todayString = dateFormatter.format(new Date(), {
      dateStyle: 'short',
    });
    const dateString = dateFormatter.format(value, {
      dateStyle: 'short',
    });
    const stringToDispatch = todayString === dateString ? null : dateString;
    context.commit('cancellationDate', value);
    context.dispatch('updateFormField', {
      name: 'opzegdatum',
      value: stringToDispatch,
    }).then(() => context.commit('updateFormFieldValidity', {
      name: 'opzegdatum',
      valid: true,
    }));
  },
  updateTotalPrice: (context) => {
    const payment = context.getters.getPayments[context.getters.getFormField('product')];
    const getPrice = () => {
      if (context.getters.getFormField('optionalProduct')) {
        return (
          parseInt(payment.price.replace(',', ''), 10)
          + parseInt(context.getters.getOptionalProduct.price.replace(',', ''), 10)
        );
      }
      return parseInt(payment.price.replace(',', ''), 10);
    };
    context.commit('totalPrice', payment ? getPrice() / 100 : null);
  },
  updateReasonToCancel: (context, required) => {
    // Reset text field
    context.commit('updateFormField', {
      name: 'reasonToCancel',
      value: null,
    });
    context.commit('reasonToCancelRequired', required);
    context.commit('updateFormFieldValidity', {
      name: 'reasonToCancel',
      valid: !required,
    });
  },
};
